import React, { useState } from 'react';
import { useWarehouse } from '../contexts/WarehouseContext';

const RemoveItemsDialog = ({ isOpen, onClose, onRemove }) => {
  const { items, removeItems } = useWarehouse();
  const [removeQuantities, setRemoveQuantities] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState('');

  // Filter items based on search term
  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.category && item.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleRemove = async () => {
    // Clear any previous errors
    setError('');
    
    const validItems = Object.entries(removeQuantities).filter(([itemId, quantity]) => 
      quantity && 
      !isNaN(parseFloat(quantity)) && 
      parseFloat(quantity) > 0 && 
      parseFloat(quantity) <= parseFloat(items.find(i => i.id === itemId)?.quantity || 0)
    );
    
    if (validItems.length === 0) {
      setError('Παρακαλώ εισάγετε έγκυρες ποσότητες για αφαίρεση');
      // Auto-dismiss error after 5 seconds
      setTimeout(() => setError(''), 5000);
      return;
    }
    
    setIsRemoving(true);
    
    try {
      // Transform the data to match what removeItems expects
      const itemsToRemove = validItems.map(([itemId, quantity]) => {
        const item = items.find(i => i.id === itemId);
        return {
          id: itemId,
          name: item.name,
          unit: item.unit,
          currentQuantity: item.quantity,
          quantityToRemove: quantity
        };
      });
      
      if (onRemove) {
        // Use custom handler (for medical records)
        await onRemove(itemsToRemove);
      } else {
        // Use default warehouse context handler (for sidebar)
        const result = await removeItems(itemsToRemove);
        if (!result.success) {
          setError(result.message);
          setIsRemoving(false);
          return;
        }
      }
      
      setRemoveQuantities({});
      setSearchTerm('');
      setError('');
      onClose();
      
    } catch (error) {
      console.error('Error removing items:', error);
      setError('Σφάλμα κατά την αφαίρεση προϊόντων');
      // Auto-dismiss error after 5 seconds
      setTimeout(() => setError(''), 5000);
    }
    
    setIsRemoving(false);
  };

  const handleClose = () => {
    setRemoveQuantities({});
    setSearchTerm('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={handleClose}>
      <div className="rounded-xl border max-w-4xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#111827', borderColor: '#374151' }}>
        <div className="p-6 border-b flex-shrink-0" style={{ borderColor: '#374151' }}>
          <h2 className="text-xl font-bold text-white">Αφαίρεση Προϊόντων</h2>
          <p className="text-gray-400 text-sm mt-1">Εισάγετε τις ποσότητες που θέλετε να αφαιρέσετε από κάθε προϊόν</p>
          
          {/* Search Filter */}
          <div className="mt-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Αναζήτηση προϊόντων..."
              className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-red-400/30"
              style={{ backgroundColor: '#374151', borderColor: '#4B5563' }}
            />
          </div>
          
          {/* Error Message */}
          {error && (
            <div className="mt-4 p-3 rounded-lg border" style={{ backgroundColor: '#7F1D1D', borderColor: '#DC2626' }}>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-red-200 text-sm">{error}</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          <div className="space-y-4">
            {searchTerm && (
              <div className="text-sm text-gray-400 mb-4">
                Εμφανίζονται {filteredItems.length} από {items.length} προϊόντα
              </div>
            )}
            {filteredItems.map((item) => (
              <div key={item.id} className="flex items-center gap-4 p-3 rounded-lg" style={{ backgroundColor: '#1F2937' }}>
                <div className="flex-1">
                  <p className="text-white font-medium">{item.name}</p>
                  <p className="text-gray-400 text-sm">
                    Διαθέσιμο: {item.quantity} {item.unit}
                    {item.category && ` • ${item.category}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-gray-300 text-sm whitespace-nowrap">
                    Αφαίρεση ({item.unit}):
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={item.quantity}
                    value={removeQuantities[item.id] || ''}
                    onChange={(e) => setRemoveQuantities(prev => ({
                      ...prev,
                      [item.id]: e.target.value
                    }))}
                    placeholder="0"
                    className="w-24 px-3 py-1 rounded border text-white focus:outline-none focus:ring-2 focus:ring-red-400/30"
                    style={{ backgroundColor: '#374151', borderColor: '#4B5563' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 border-t flex gap-3 flex-shrink-0" style={{ borderColor: '#374151' }}>
          <button
            onClick={handleRemove}
            className="flex-1 px-5 py-2.5 rounded-lg font-medium text-white transition-transform duration-200 hover:scale-[1.02] disabled:opacity-50"
            style={{ background: 'linear-gradient(90deg, #059669, #047857)' }}
            disabled={isRemoving}
          >
            {isRemoving ? 'Χρήση...' : 'Χρήση'}
          </button>
          <button 
            onClick={handleClose}
            className="flex-1 px-5 py-2.5 rounded-lg font-medium text-white transition-colors"
            style={{ backgroundColor: '#4A5568' }}
            disabled={isRemoving}
          >
            Ακύρωση
          </button>
        </div>
      </div>
    </div>
  );
};

export default RemoveItemsDialog;
